import React, { createContext, useState, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import * as authApi from '../api/auth';
import type { User, LoginCredentials } from '../api/types';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { estErreurReseau, rafraichirJeton } from '../api/client';

// Type du Contexte d'authentification
interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** Le serveur n'a pas répondu : les données affichées peuvent être périmées. */
  serveurInjoignable: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Créer le Contexte
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Fournisseur de Contexte
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [serveurInjoignable, setServeurInjoignable] = useState<boolean>(false);

  // Fonction de déconnexion instantanée côté client
  const handleLogout = async (): Promise<void> => {
    try {
      authApi.logout().catch(() => {});
    } finally {
      try {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('user');
      } catch {}
      setUser(null);
      setLoading(false);
    }
  };

  // Déconnexion automatique après inactivité (configurable via env, 30 min par défaut)
  const idleTimeoutMinutes = parseInt(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES || '30', 10);
  useIdleTimeout({
    onIdle: () => {
      if (user) {
        console.log(`🔒 Déconnexion automatique après ${idleTimeoutMinutes} minutes d'inactivité`);
        handleLogout();
        // Rediriger vers la page de connexion avec un message
        window.location.href = '/login?reason=timeout';
      }
    },
    idleTime: idleTimeoutMinutes * 60 * 1000, // En millisecondes
    enabled: !!user, // Activer seulement si l'utilisateur est connecté
  });

  // Charger l'utilisateur au démarrage (flux robuste)
  useEffect(() => {
    const initAuth = async () => {
      try {
        const access = localStorage.getItem('access');
        const refresh = localStorage.getItem('refresh');

        if (access) {
          const currentUser = authApi.getCurrentUser();
          if (currentUser) setUser(currentUser);
          setLoading(false);
          try {
            const freshUser = await authApi.getProfile();
            setUser(freshUser);
          } catch (error) {
            // Serveur injoignable : la session reste probablement valide. On
            // garde l'utilisateur en cache et les jetons — les effacer
            // déconnecterait pour une simple coupure réseau.
            if (estErreurReseau(error)) {
              setServeurInjoignable(true);
              return;
            }

            // Essai de refresh si profil échoue
            if (refresh) {
              try {
                await rafraichirJeton(refresh);
                const freshUserAfter = await authApi.getProfile();
                setUser(freshUserAfter);
              } catch (erreurRefresh) {
                if (estErreurReseau(erreurRefresh)) {
                  setServeurInjoignable(true);
                  return;
                }
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                localStorage.removeItem('user');
                window.location.href = '/login';
              }
            } else {
              localStorage.removeItem('access');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }
          }
          return;
        }

        if (!access && refresh) {
          try {
            await rafraichirJeton(refresh);
            try {
              const freshUser = await authApi.getProfile();
              setUser(freshUser);
            } catch {
              const currentUser = authApi.getCurrentUser();
              if (currentUser) setUser(currentUser);
            }
            setLoading(false);
            return;
          } catch (erreurRefresh) {
            if (estErreurReseau(erreurRefresh)) {
              setServeurInjoignable(true);
              setLoading(false);
              return;
            }
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
            localStorage.removeItem('user');
          }
        }

        setLoading(false);
      } catch {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Fonction de connexion
  const login = async (credentials: LoginCredentials): Promise<void> => {
    setLoading(true);
    try {
      const response = await authApi.login(credentials);
      setUser(response.user);
    } finally {
      setLoading(false);
    }
  };

  // Rafraîchir les données utilisateur
  const refreshUser = async (): Promise<void> => {
    const freshUser = await authApi.getProfile();
    setUser(freshUser);
  };

  const value: AuthContextType = {
    user,
    loading,
    serveurInjoignable,
    login,
    logout: handleLogout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {serveurInjoignable && (
        <div className="bg-error text-error-content text-sm px-4 py-2 flex items-center justify-center gap-3">
          <span>
            <strong>Serveur injoignable.</strong> Les informations affichées peuvent être
            incomplètes ou périmées — aucune donnée n'est perdue.
          </span>
          <button
            className="btn btn-xs"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};